/**
 * Data tables (ADR-0106): small organisation tables agents read and keep and people edit on the
 * Data tables screen (the datatables tool's people-facing routes).
 */
import type { Http } from "./http";
import type { Text } from "./types";

const enc = encodeURIComponent;

export type DataColumnKind = "text" | "number" | "date" | "yes_no";
export interface DataColumn {
  name: string;
  label: Text;
  kind: DataColumnKind;
}
export interface DataTableInfo {
  name: string;
  title: Text;
  description: Text;
  columns: DataColumn[];
  key_column: string;
  agents_may_write: boolean;
  created_at: string | null;
  created_by: string;
  updated_at: string | null;
  row_count: number;
}
export interface DataRow {
  key: string;
  values: Record<string, string | number | boolean | null>;
  version: number;
  updated_at: string;
  updated_by: string;
  /** person | agent */
  updated_via: string;
  agent: string | null;
  run_id: string | null;
}
export interface DataChange {
  key: string;
  change: "added" | "changed" | "removed" | "restored";
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
  by: string;
  via: string;
  agent: string | null;
  run_id: string | null;
}
export interface DataTableIn {
  name: string;
  title: Text;
  description?: Text;
  columns: DataColumn[];
  key_column: string;
  agents_may_write: boolean;
}

/** A table name from its English title ("Reminders sent" → `reminders_sent`). */
export function tableName(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "t_$1")
    .slice(0, 60);
  return base.length >= 2 ? base : "";
}

export function dataTablesApi(http: Http) {
  return {
    list: () => http.get<DataTableInfo[]>("datatables", "/v1/data-tables"),
    create: (body: DataTableIn) => http.post<DataTableInfo>("datatables", "/v1/data-tables", body),
    update: (name: string, body: Partial<Pick<DataTableIn, "title" | "description" | "columns" | "agents_may_write">>) => http.patch<DataTableInfo>("datatables", `/v1/data-tables/${enc(name)}`, body),
    remove: (name: string) => http.delete<void>("datatables", `/v1/data-tables/${enc(name)}`),
    rows: (name: string, q: { q?: string; limit?: number; offset?: number } = {}) => http.get<{ rows: DataRow[]; total: number }>("datatables", `/v1/data-tables/${enc(name)}/rows`, q),
    putRow: (name: string, key: string, values: DataRow["values"]) => http.put<DataRow & { created: boolean }>("datatables", `/v1/data-tables/${enc(name)}/rows/${enc(key)}`, { values }),
    removeRow: (name: string, key: string) => http.delete<void>("datatables", `/v1/data-tables/${enc(name)}/rows/${enc(key)}`),
    importCsv: (name: string, csv: string, mode: "add" | "replace") => http.post<{ rows: number; added: number; changed: number }>("datatables", `/v1/data-tables/${enc(name)}/import`, { csv, mode }),
    exportCsv: async (name: string): Promise<string> => (await http.raw("datatables", `/v1/data-tables/${enc(name)}/export`)).text(),
    changes: (name: string, key?: string) => http.get<DataChange[]>("datatables", `/v1/data-tables/${enc(name)}/changes`, { key }),
  };
}
